import React from "react";
import {
  Boxes,
  ChevronDown,
  ChevronUp,
  AlertOctagon,
  Activity,
  Clock,
  Coins,
  ShieldCheck,
  CircleDot,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { fetchBundles } from "../lib/api";
import { shortAddr } from "../lib/utils";
import { CopyButton } from "./CopyButton";

/**
 * Status → { variant, icon, label } for the top-level Bundle Status.
 * Variants map onto Tailwind tokens already defined in badge.js.
 *
 * danger  → red    (HYBRID_STRONG)
 * warning → orange (HYBRID_WEAK)
 * primary → yellow-ish accent (TEMPORAL_ONLY / FUNDING_ONLY)
 * success → green  (COORDINATION_LOW_IMPACT / UNCOORDINATED)
 */
function statusMeta(status) {
  switch (status) {
    case "HYBRID_STRONG":
      return { variant: "danger", icon: AlertOctagon, label: "Hybrid Strong" };
    case "HYBRID_WEAK":
      return { variant: "warning", icon: Activity, label: "Hybrid Weak" };
    case "TEMPORAL_ONLY":
      return { variant: "primary", icon: Clock, label: "Temporal Only" };
    case "FUNDING_ONLY":
      return { variant: "primary", icon: Coins, label: "Funding Only" };
    case "COORDINATION_LOW_IMPACT":
      return { variant: "success", icon: ShieldCheck, label: "Low Impact" };
    case "UNCOORDINATED":
      return { variant: "success", icon: ShieldCheck, label: "Uncoordinated" };
    default:
      return { variant: "muted", icon: CircleDot, label: status || "Unknown" };
  }
}

function tierMeta(tier) {
  switch (tier) {
    case "VERY_HIGH":
      return { variant: "danger", label: "Very High" };
    case "HIGH":
      return { variant: "warning", label: "High" };
    case "MEDIUM":
      return { variant: "primary", label: "Medium" };
    case "LOW":
      return { variant: "success", label: "Low" };
    default:
      return { variant: "muted", label: tier || "Unknown" };
  }
}

function fmtPct(value, decimals = 2) {
  if (value === undefined || value === null || isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(decimals)}%`;
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
        {label}
      </div>
      <div className="mt-0.5 text-base font-semibold tabular-nums">{value}</div>
      {sub ? (
        <div className="text-[11px] text-[hsl(var(--muted))]">{sub}</div>
      ) : null}
    </div>
  );
}

function ClusterRow({ cluster, index }) {
  const [open, setOpen] = React.useState(false);
  const tm = tierMeta(cluster.tier);
  const walletCount =
    cluster.wallet_count ??
    (Array.isArray(cluster.wallets) ? cluster.wallets.length : null);

  return (
    <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[hsl(var(--primary)/0.04)] transition-colors"
        data-testid={`bundle-cluster-row-${index}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] text-[hsl(var(--muted))] font-mono tabular-nums w-6 shrink-0">
            #{index + 1}
          </span>
          <Badge variant={tm.variant}>{tm.label}</Badge>
          <span className="text-xs text-[hsl(var(--muted))] truncate">
            {cluster.type || "—"}
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs font-semibold tabular-nums">
            {fmtPct(cluster.bri, 0)} BRI
          </span>
          <span className="text-[11px] text-[hsl(var(--muted))] tabular-nums">
            {walletCount ?? "?"}w
          </span>
          <span className="text-[11px] text-[hsl(var(--muted))] tabular-nums">
            {fmtPct(cluster.supply_pct, 2)}
          </span>
          {open ? (
            <ChevronUp size={14} className="text-[hsl(var(--muted))]" />
          ) : (
            <ChevronDown size={14} className="text-[hsl(var(--muted))]" />
          )}
        </div>
      </button>
      {open ? (
        <div className="border-t border-[hsl(var(--border))] px-3 py-2 space-y-2">
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div>
              <div className="text-[hsl(var(--muted))] uppercase tracking-wider">
                BRI
              </div>
              <div className="font-semibold tabular-nums">
                {fmtPct(cluster.bri, 0)}
              </div>
            </div>
            <div>
              <div className="text-[hsl(var(--muted))] uppercase tracking-wider">
                Supply
              </div>
              <div className="font-semibold tabular-nums">
                {fmtPct(cluster.supply_pct, 2)}
              </div>
            </div>
            <div>
              <div className="text-[hsl(var(--muted))] uppercase tracking-wider">
                Wallets
              </div>
              <div className="font-semibold tabular-nums">
                {walletCount ?? "—"}
              </div>
            </div>
          </div>
          {Array.isArray(cluster.wallets) && cluster.wallets.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))] mb-1">
                Wallets
              </div>
              <ul className="space-y-1">
                {cluster.wallets.slice(0, 10).map((addr) => (
                  <li
                    key={addr}
                    className="flex items-center justify-between gap-2 text-[11px] font-mono"
                  >
                    <span className="truncate">{shortAddr(addr)}</span>
                    <CopyButton value={addr} />
                  </li>
                ))}
                {cluster.wallets.length > 10 ? (
                  <li className="text-[11px] text-[hsl(var(--muted))]">
                    + {cluster.wallets.length - 10} more
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function BundleAnalysisCard({ mint }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!mint) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    fetchBundles(mint)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) {
          const msg =
            e?.response?.data?.detail || e?.message || "Bundle analysis unavailable.";
          setError(String(msg));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mint]);

  return (
    <Card data-testid="bundle-analysis-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Boxes size={16} className="text-[hsl(var(--primary))]" />
            <CardTitle>Bundle Analysis</CardTitle>
          </div>
          {data ? (
            <StatusBadge status={data.status} />
          ) : null}
        </div>
        <CardDescription>
          Bundle Risk Index (BRI) — detects coordinated wallet clusters by
          temporal and funding heuristics. Higher BRI means stronger coordination
          signals.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-xs text-[hsl(var(--muted))] py-2">
            Loading bundle analysis…
          </div>
        ) : error ? (
          <div className="text-xs text-[hsl(var(--warning))] py-2">{error}</div>
        ) : !data || !data.total_clusters ? (
          <div className="text-xs text-[hsl(var(--muted))] py-2">
            No coordinated clusters detected.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat
                label="Risky Supply"
                value={fmtPct(data.risky_supply_pct, 2)}
              />
              <Stat
                label="Clusters"
                value={data.total_clusters ?? 0}
                sub={`${data.high_clusters ?? 0} HIGH`}
              />
              <Stat label="Avg BRI" value={fmtPct(data.avg_bri, 0)} />
              <Stat
                label="Top Tier"
                value={
                  data.clusters?.[0]
                    ? tierMeta(data.clusters[0].tier).label
                    : "—"
                }
                sub={
                  data.clusters?.[0]
                    ? `${fmtPct(data.clusters[0].bri, 0)} BRI`
                    : null
                }
              />
            </div>

            <Separator />

            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))] mb-2">
                Clusters ({data.clusters?.length ?? 0})
              </div>
              <div className="space-y-2">
                {(data.clusters || []).map((c, i) => (
                  <ClusterRow key={i} cluster={c} index={i} />
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const meta = statusMeta(status);
  const Icon = meta.icon;
  return (
    <Badge variant={meta.variant} className="gap-1">
      <Icon size={11} />
      {meta.label}
    </Badge>
  );
}
